import { useState, useEffect, useRef } from 'react'
import { Modal } from '@/shared/components/Modal'
import { filesApi } from '../api/files'
import { fileName, getErrorMessage } from '@/shared/api/generated'
import type { S3Object } from '@/shared/api/generated'
import { toast } from '@/shared/stores/toastStore'

type PreviewType = 'image' | 'pdf' | 'docx' | 'html' | 'markdown' | 'text' | null

const imageExts = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'])
const textExts = new Set([
  'txt', 'log', 'csv', 'json', 'yaml', 'yml', 'xml', 'toml', 'ini', 'cfg', 'conf',
  'sh', 'bash', 'zsh', 'py', 'go', 'rs', 'ts', 'tsx', 'js', 'jsx', 'css', 'sql',
  'java', 'kt', 'c', 'cpp', 'h', 'hpp', 'rb', 'php', 'lua', 'r', 'swift', 'scala',
  'tf', 'hcl', 'env', 'properties', 'makefile', 'dockerfile',
])

function getExt(name: string): string {
  return name.split('.').pop()?.toLowerCase() ?? ''
}

export function getPreviewType(name: string): PreviewType {
  const ext = getExt(name)
  if (imageExts.has(ext)) return 'image'
  if (ext === 'pdf') return 'pdf'
  if (ext === 'docx') return 'docx'
  if (ext === 'doc') return 'text'
  if (ext === 'html' || ext === 'htm') return 'html'
  if (ext === 'md' || ext === 'markdown') return 'markdown'
  if (textExts.has(ext)) return 'text'
  return null
}

export function canPreview(name: string): boolean {
  return getPreviewType(name) !== null
}

interface FilePreviewProps {
  obj: S3Object
  groupId: number
  onClose: () => void
  onDownload?: () => void
}

export function FilePreview({ obj, groupId, onClose, onDownload }: FilePreviewProps) {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [maximized, setMaximized] = useState(false)
  const name = fileName(obj)
  const type = getPreviewType(name)

  useEffect(() => {
    let cancelled = false
    filesApi.getPreviewUrl({ path: obj.key, groupID: groupId })
      .then(u => { if (!cancelled) setUrl(u) })
      .catch(err => { if (!cancelled) setError(getErrorMessage(err)) })
    return () => { cancelled = true }
  }, [obj.key, groupId])

  return (
    <Modal title={name} onClose={onClose} wide>
      <div className={`file-preview${maximized ? ' maximized' : ''}`}>
        <div className="preview-actions">
          <button className="btn-icon" onClick={() => setMaximized(!maximized)} title={maximized ? 'Restore' : 'Maximize'}>
            {maximized ? '⊡' : '⊞'}
          </button>
          {onDownload && <button className="btn-secondary" onClick={onDownload}>⬇ Download</button>}
        </div>
        {error && <div className="preview-error">{error}</div>}
        {!url && !error && <div className="preview-loading">Loading…</div>}
        {url && type === 'image' && <img src={url} alt={name} className="preview-image" />}
        {url && type === 'pdf' && <iframe src={url} title={name} className="preview-pdf" />}
        {url && type === 'docx' && <DocxPreview url={url} />}
        {url && type === 'html' && <HtmlPreview url={url} />}
        {url && type === 'markdown' && <MarkdownPreview url={url} />}
        {url && type === 'text' && <TextPreview url={url} />}
      </div>
    </Modal>
  )
}

function DocxPreview({ url }: { url: string }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { renderAsync } = await import('docx-preview')
        const resp = await fetch(url)
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        const blob = await resp.blob()
        if (containerRef.current && !cancelled) {
          await renderAsync(blob, containerRef.current, undefined, { className: 'docx-wrapper' })
        }
      } catch (err) {
        if (!cancelled) setError(getErrorMessage(err))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [url])

  if (error) return <div className="preview-error">{error}</div>
  return (
    <>
      {loading && <div className="preview-loading">Rendering document…</div>}
      <div ref={containerRef} className="preview-docx" />
    </>
  )
}

function TextPreview({ url }: { url: string }) {
  const [content, setContent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch(url)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text() })
      .then(t => { if (!cancelled) setContent(t) })
      .catch(err => { if (!cancelled) setError(getErrorMessage(err)) })
    return () => { cancelled = true }
  }, [url])

  if (error) return <div className="preview-error">{error}</div>
  if (content === null) return <div className="preview-loading">Loading…</div>
  return <pre className="preview-text">{content}</pre>
}

function HtmlPreview({ url }: { url: string }) {
  const [content, setContent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch(url)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text() })
      .then(t => { if (!cancelled) setContent(t) })
      .catch(err => { if (!cancelled) setError(getErrorMessage(err)) })
    return () => { cancelled = true }
  }, [url])

  if (error) return <div className="preview-error">{error}</div>
  if (content === null) return <div className="preview-loading">Loading…</div>
  return <iframe srcDoc={content} title="HTML preview" className="preview-pdf" sandbox="allow-same-origin" />
}

function MarkdownPreview({ url }: { url: string }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [{ marked }, resp] = await Promise.all([
          import('marked'),
          fetch(url),
        ])
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        const md = await resp.text()
        if (cancelled) return

        const html = await marked.parse(md)
        if (!containerRef.current || cancelled) return
        containerRef.current.innerHTML = html

        // Render mermaid blocks: marked outputs <code class="language-mermaid">
        const mermaidBlocks = containerRef.current.querySelectorAll('code.language-mermaid')
        if (mermaidBlocks.length > 0) {
          try {
            const mermaid = (await import('mermaid')).default
            mermaid.initialize({ startOnLoad: false, theme: 'default' })
            for (let i = 0; i < mermaidBlocks.length; i++) {
              const code = mermaidBlocks[i]!
              const pre = code.parentElement
              if (!pre || pre.tagName !== 'PRE') continue
              const { svg } = await mermaid.render(`mermaid-${i}`, code.textContent ?? '')
              const div = document.createElement('div')
              div.className = 'mermaid'
              div.innerHTML = svg
              pre.replaceWith(div)
            }
          } catch (err) {
            toast.error('Mermaid rendering failed: ' + getErrorMessage(err))
          }
        }
      } catch (err) {
        if (!cancelled) setError(getErrorMessage(err))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [url])

  if (error) return <div className="preview-error">{error}</div>
  return (
    <>
      {loading && <div className="preview-loading">Rendering…</div>}
      <div ref={containerRef} className="preview-markdown" />
    </>
  )
}
